// src/components/LihtcCsvUpload.js
// Portfolio CSV intake for the LIHTC utility allowance queue.
//
// Parses in the browser and posts JSON to /api/utility-allowance/lihtc/intake/bulk,
// so the service needs no CSV dependency. Nothing is computed on upload — rows
// land in the work queue and the schedules are produced separately with
// live-sourced tariffs.
//
// The number that matters in the result is townsNeedingRates: tariff sourcing
// scales with towns, not properties, so 70 properties across 6 new towns is
// six jobs, not seventy.

import React, { useRef, useState } from 'react';

const COLUMNS = [
  'property_name', 'address', 'town', 'state', 'bin', 'placed_in_service', 'units',
  'assistance', 'method', 'has_voucher_holders', 'structure_idx', 'bedrooms',
  'heating', 'water_heating', 'cooking', 'other_electric', 'ac', 'water_sewer', 'trash',
  'heating_fuel', 'water_heat_fuel', 'cooking_fuel', 'trash_monthly', 'notice_date',
  'contact_name', 'contact_email', 'contact_org', 'notes',
];

const EXAMPLE = [
  'Derby Lofts', '10 Derby St', 'Salem', 'MA', 'MA-26-00123', '2019-06-01', '48',
  'none', 'husm', 'no', '5', '1,2,3',
  'tenant', 'owner', 'tenant', 'tenant', 'tenant', 'submetered', 'owner',
  'gas', '', 'electric', '', '',
  'Jane Doe', 'jane@example.com', 'Example Management', 'first cycle',
];

const HELP = [
  ['assistance', 'rd | hud | none — whether the building gets RD or HUD rental assistance'],
  ['method', 'husm | pha | utility_estimate | energy_model | agency'],
  ['structure_idx', '1 Manufactured · 2 Single Family Detached · 3 Attached/Townhouse · 4 Low-Rise 2-4 · 5 High-Rise 5+'],
  ['bedrooms', 'comma list, 0-5 (0 or "studio" for studios), e.g. 1,2,3'],
  ['heating … trash', 'tenant | submetered | owner — who pays that service'],
  ['*_fuel', 'gas | electric | heatpump | oil | propane (heat only) — leave blank if owner-paid'],
  ['trash_monthly', 'actual monthly charge when the tenant pays trash; never estimated'],
];

const csvEscape = (v) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));

// Minimal RFC-4180-ish parser: quoted fields, escaped quotes, CRLF.
function parseCsv(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 1; } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else if (c !== '\r') field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

const yes = (v) => /^(y|yes|true|1)$/i.test(String(v || '').trim());

function toPayload(headers, cells) {
  const g = (name) => {
    const i = headers.indexOf(name);
    return i === -1 ? '' : (cells[i] || '').trim();
  };
  const svc = (v) => {
    const s = v.toLowerCase();
    if (!s) return '';
    if (['tenant', 'submetered', 'owner'].includes(s)) return s;
    if (s === 'sub-metered' || s === 'sub metered') return 'submetered';
    return v; // pass through so the server reports it as invalid with the row number
  };
  return {
    propertyName: g('property_name'), address: g('address'), town: g('town'), state: g('state'),
    bin: g('bin'), placedInService: g('placed_in_service'), units: g('units'),
    assistance: g('assistance').toLowerCase(), method: g('method').toLowerCase(),
    hasVoucherHolders: yes(g('has_voucher_holders')),
    structureIdx: g('structure_idx'), bedrooms: g('bedrooms'),
    services: {
      heating: svc(g('heating')), waterHeating: svc(g('water_heating')), cooking: svc(g('cooking')),
      otherElectric: svc(g('other_electric')), ac: svc(g('ac')), waterSewer: svc(g('water_sewer')), trash: svc(g('trash')),
    },
    equipment: {
      heatingFuel: g('heating_fuel').toLowerCase(), waterHeatFuel: g('water_heat_fuel').toLowerCase(),
      cookingFuel: g('cooking_fuel').toLowerCase(),
    },
    trashMonthly: g('trash_monthly'), noticeDate: g('notice_date'),
    contactName: g('contact_name'), contactEmail: g('contact_email'), contactOrg: g('contact_org'),
    notes: g('notes'),
  };
}

export default function LihtcCsvUpload({ api, T, card, h3, mobile }) {
  const fileRef = useRef(null);
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const downloadTemplate = () => {
    const csv = `${COLUMNS.join(',')}\n${EXAMPLE.map(csvEscape).join(',')}\n`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'lihtc-utility-allowance-template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    setParsed(null); setParseError(null); setResult(null);
    if (!file) return;
    try {
      const rows = parseCsv(await file.text());
      if (rows.length < 2) throw new Error('The file needs a header row and at least one property.');
      const headers = rows[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, '_'));
      if (!headers.includes('property_name')) throw new Error('No property_name column found — start from the template.');
      const unknown = headers.filter((h) => h && !COLUMNS.includes(h));
      const properties = rows.slice(1).map((cells) => toPayload(headers, cells));
      setParsed({ fileName: file.name, headers, unknown, properties });
    } catch (err) {
      setParseError(err.message);
    }
  };

  const upload = async () => {
    if (!parsed) return;
    setUploading(true); setResult(null);
    try {
      const r = await fetch(`${api}/api/utility-allowance/lihtc/intake/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ properties: parsed.properties }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.message || (body.rejected ? 'Every row had a problem — see below.' : 'Upload failed.'));
      setResult(body);
      if (fileRef.current) fileRef.current.value = '';
      setParsed(null);
    } catch (err) {
      setParseError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const noteStyle = { fontSize: '0.8rem', color: T.gray500, lineHeight: 1.55 };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: T.gray800, marginBottom: '0.35rem' };
  const pillBtn = {
    padding: '0.85rem', background: T.blue600, color: T.white, border: 'none', borderRadius: T.radiusPill,
    fontWeight: 700, fontSize: '0.95rem', fontFamily: T.font, width: '100%', transition: 'background 0.2s ease',
  };

  return (
    <>
      <div style={card}>
        <h3 style={h3}>Portfolio upload</h3>
        <p style={{ ...noteStyle, margin: '0.85rem 0 1rem' }}>
          One row per property per configuration &mdash; a property with both townhouses and a mid-rise,
          or different metering building to building, needs a row for each. Rows are queued, not computed:
          the schedules are produced separately against tariffs sourced for each town.
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
          <button type="button" onClick={downloadTemplate} style={{
            padding: '0.55rem 1.1rem', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: T.font,
            background: T.white, color: T.blue600, border: `1px solid ${T.blue600}`, borderRadius: T.radiusPill,
            transition: 'background 0.2s ease',
          }}
            onMouseOver={e => { e.currentTarget.style.background = T.blue50; }}
            onMouseOut={e => { e.currentTarget.style.background = T.white; }}
          >Download template CSV</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile}
            style={{ fontSize: '0.85rem', color: T.gray600 }} />
        </div>

        <details>
          <summary style={{ fontSize: '0.82rem', color: T.gray800, cursor: 'pointer', fontWeight: 600 }}>Column reference</summary>
          <div style={{ marginTop: '0.6rem' }}>
            {HELP.map(([col, desc]) => (
              <div key={col} style={{ ...noteStyle, marginBottom: '0.25rem' }}>
                <code style={{ color: T.gray900, background: T.gray100, padding: '0.1rem 0.35rem', borderRadius: '4px' }}>{col}</code> &mdash; {desc}
              </div>
            ))}
            <div style={{ ...noteStyle, marginTop: '0.5rem' }}>
              Towns without cited tariffs on file are accepted &mdash; they queue as &ldquo;rates needed&rdquo; rather than being rejected.
            </div>
          </div>
        </details>

        {parseError && (
          <div style={{ marginTop: '1rem', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: T.radiusSm, padding: '0.75rem 1rem', fontSize: '0.85rem' }}>
            {parseError}
          </div>
        )}

        {parsed && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ ...labelStyle, marginBottom: '0.4rem' }}>
              {parsed.fileName} &mdash; {parsed.properties.length} {parsed.properties.length === 1 ? 'property' : 'properties'} read
            </div>
            {parsed.unknown.length > 0 && (
              <div style={{ ...noteStyle, color: '#92400e', marginBottom: '0.5rem' }}>
                Ignored columns: {parsed.unknown.join(', ')}
              </div>
            )}
            <div style={{ overflowX: 'auto', border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm, marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: T.gray100 }}>
                    {['Property', 'Town', 'Beds', 'Assistance', 'Method'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '0.45rem 0.7rem', color: T.gray600, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.properties.slice(0, 8).map((p, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${T.gray100}` }}>
                      <td style={{ padding: '0.4rem 0.7rem', color: T.gray900 }}>{p.propertyName || <em style={{ color: '#b91c1c' }}>missing</em>}</td>
                      <td style={{ padding: '0.4rem 0.7rem', color: T.gray600 }}>{p.town || '—'}</td>
                      <td style={{ padding: '0.4rem 0.7rem', color: T.gray600 }}>{p.bedrooms || '—'}</td>
                      <td style={{ padding: '0.4rem 0.7rem', color: T.gray600 }}>{p.assistance || '—'}</td>
                      <td style={{ padding: '0.4rem 0.7rem', color: T.gray600 }}>{p.method || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsed.properties.length > 8 && (
              <div style={{ ...noteStyle, marginBottom: '1rem' }}>…and {parsed.properties.length - 8} more.</div>
            )}
            <button type="button" onClick={upload} disabled={uploading} style={{
              ...pillBtn, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1,
            }}
              onMouseOver={e => { if (!uploading) e.currentTarget.style.background = '#1d4ed8'; }}
              onMouseOut={e => { e.currentTarget.style.background = T.blue600; }}
            >
              {uploading ? 'Uploading…' : `Queue ${parsed.properties.length} ${parsed.properties.length === 1 ? 'property' : 'properties'}`}
            </button>
          </div>
        )}
      </div>

      {result && (
        <div style={{ ...card, borderLeft: `4px solid ${T.blue600}` }}>
          <h3 style={h3}>Queued</h3>
          <p style={{ fontSize: '0.9rem', color: T.gray800, margin: '0.85rem 0 0.75rem' }}>
            {result.saved} {result.saved === 1 ? 'property' : 'properties'} added to the queue.
          </p>

          {result.townsNeedingRates?.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: T.radiusSm, padding: '0.8rem 1rem', marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#92400e', marginBottom: '0.3rem', fontFamily: T.font }}>
                Tariffs needed for {result.townsNeedingRates.length} {result.townsNeedingRates.length === 1 ? 'town' : 'towns'}
              </div>
              <div style={{ fontSize: '0.84rem', color: '#92400e', lineHeight: 1.6 }}>{result.townsNeedingRates.join(' · ')}</div>
              <div style={{ fontSize: '0.78rem', color: '#92400e', marginTop: '0.4rem', lineHeight: 1.6 }}>
                Sourcing is per town, not per property &mdash; these are the jobs that have to happen before those properties can be priced.
              </div>
            </div>
          )}

          {result.blocked > 0 && (
            <div style={{ ...noteStyle, marginBottom: '0.75rem' }}>
              {result.blocked} {result.blocked === 1 ? 'property is' : 'properties are'} flagged as blocked &mdash; RD or HUD assisted,
              or electing a method other than the HUD Utility Schedule Model. Those take the allowance their program prescribes.
            </div>
          )}

          {result.rejected?.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: T.radiusSm, padding: '0.8rem 1rem' }}>
              <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#b91c1c', marginBottom: '0.4rem' }}>
                {result.rejected.length} {result.rejected.length === 1 ? 'row' : 'rows'} skipped
              </div>
              {result.rejected.map((r) => (
                <div key={r.row} style={{ fontSize: '0.82rem', color: '#b91c1c', lineHeight: 1.6 }}>
                  Row {r.row}{r.name ? ` (${r.name})` : ''}: {r.errors.join('; ')}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
